import type {
  Keypair,
  PublicKey,
  Transaction,
  TransactionInstruction,
  VersionedTransaction,
} from "@solana/web3.js";

import type { IndexerAsset } from "./tokenAssets";

import * as impl from "./arcaneProgram.impl.js";

export const getProgramId = impl.getProgramId as () => PublicKey;

export const getPoolPDA = impl.getPoolPDA as (
  denominationLamports: number | bigint
) => [PublicKey, number];

export const getTokenPoolPDA = impl.getTokenPoolPDA as (
  mint: PublicKey | string,
  denominationSmallestUnits: number | bigint
) => [PublicKey, number];

export const getTreasuryPDA = impl.getTreasuryPDA as () => [PublicKey, number];

export const getCommitmentPDA = impl.getCommitmentPDA as (
  commitmentBigInt: bigint | number | string
) => [PublicKey, number];

export const getNullifierPDA = impl.getNullifierPDA as (
  nullifierHashBigInt: bigint | number | string
) => [PublicKey, number];

export const getNetworkStatePDA = impl.getNetworkStatePDA as () => [
  PublicKey,
  number,
];

/** Frontend wallet adapter or backend {@link Keypair}. */
export type EncryptKeyWallet =
  | Keypair
  | {
      signMessage: (
        message: Uint8Array
      ) => Promise<Uint8Array> | Uint8Array;
    };

export type BuildDepositTxParams = {
  senderPublicKey: PublicKey | string;
  /** Pre-generated secret note from {@link generateDepositParams}. */
  note: string;
  onChainBackup?: boolean;
  computeUnitPrice?: number;
  tempSigner?: PublicKey | string;
};

export const buildDepositTx = impl.buildDepositTx as (
  params: BuildDepositTxParams
) => Promise<{ tx: Transaction; note: string }>;

export type BuildBatchDepositTxsParams = {
  senderPublicKey: PublicKey | string;
  /** Pre-generated secret notes from {@link generateDepositParams}. */
  notes: string[];
  onChainBackup?: boolean;
  computeUnitPrice?: number;
};

export type BatchDepositTxsResult = {
  depositTx: VersionedTransaction;
  backupTx?: Transaction;
  notes: string[];
};

export const buildDepositInstructionsForNotes =
  impl.buildDepositInstructionsForNotes as (
    sender: PublicKey,
    notes: string[]
  ) => Promise<TransactionInstruction[]>;

export const buildInitNotesInstructionIfNeeded =
  impl.buildInitNotesInstructionIfNeeded as (
    sender: PublicKey
  ) => Promise<TransactionInstruction | null>;

export const buildBackupNotesInstruction = impl.buildBackupNotesInstruction as (
  sender: PublicKey,
  notes: string[]
) => Promise<TransactionInstruction>;

export const isExtendBackupPdaNeeded = impl.isExtendBackupPdaNeeded as (
  walletPubkey: PublicKey,
  newNotesLength: number
) => Promise<boolean>;

export const buildRequestHeapIx = impl.buildRequestHeapIx as () => TransactionInstruction;

export const buildDepositVersionedTransaction =
  impl.buildDepositVersionedTransaction as (
    sender: PublicKey,
    instructions: TransactionInstruction[],
    computeUnitPrice?: number
  ) => Promise<VersionedTransaction>;

export const buildBatchDepositTxs = impl.buildBatchDepositTxs as (
  params: BuildBatchDepositTxsParams
) => Promise<BatchDepositTxsResult>;

export const fetchPlatformFee = impl.fetchPlatformFee as () => Promise<string>;

export const fetchCommitments = impl.fetchCommitments as (
  denomination: number | bigint | string,
  indexerAsset?: IndexerAsset
) => Promise<unknown>;

export const validateNullifier = impl.validateNullifier as (
  nullifierHash: string,
  indexerAsset?: IndexerAsset
) => Promise<unknown>;

export const fetchTxStatus = impl.fetchTxStatus as (
  commitmentHex: string,
  nullifierHashHex: string,
  indexerAsset?: IndexerAsset
) => Promise<unknown>;

export const fetchDepositCountByCommitment =
  impl.fetchDepositCountByCommitment as (
    commitment: string,
    denomination: number | bigint | string,
    indexerAsset?: IndexerAsset
  ) => Promise<unknown>;

export type FetchWalletBalanceOptions = {
  includeDeposits?: boolean;
  indexerAsset?: IndexerAsset;
};

export const fetchWalletBalance = impl.fetchWalletBalance as (
  wallet: string,
  options?: FetchWalletBalanceOptions
) => Promise<unknown>;

export const fetchRelayers = impl.fetchRelayers as (
  network?: string
) => Promise<unknown>;

export const fetchLatestTransactions = impl.fetchLatestTransactions as (
  type?: string,
  indexerAsset?: IndexerAsset
) => Promise<unknown>;

export const fetchTxCounts = impl.fetchTxCounts as (
  type?: string,
  indexerAsset?: IndexerAsset
) => Promise<unknown>;

export const normalizePubkeyString = impl.normalizePubkeyString as (
  value: unknown
) => string;

export type BuildRelayPayloadParams = {
  args: unknown[];
  proofHex: string;
  denomination: number | bigint | string;
  withdrawAddress: unknown;
  relayerPubkey: unknown;
  mint?: unknown;
};

export type RelayPayload = Record<string, unknown>;

export const buildRelayPayload = impl.buildRelayPayload as (
  params: BuildRelayPayloadParams
) => RelayPayload;

export const submitToRelayer = impl.submitToRelayer as (
  relayerUrl: string,
  payload: RelayPayload
) => Promise<unknown>;

export const pollRelayerJob = impl.pollRelayerJob as (
  relayerUrl: string,
  jobId: string,
  maxWaitMs?: number
) => Promise<unknown>;

export const getEncryptionKey = impl.getEncryptionKey as (
  wallet: EncryptKeyWallet
) => Promise<Uint8Array>;

export const encryptData = impl.encryptData as (
  key: Uint8Array,
  plaintext: string
) => Uint8Array;

export const decryptData = impl.decryptData as (
  key: Uint8Array,
  combined: Uint8Array
) => string;
